// src/client/game/scenes/LeaderboardScene.ts

import Phaser from "phaser";

type LeaderboardEntry = {
  userName: string;
  score: number;
  rank: number;
  timeMs?: number;
};

type LeaderboardResponse = {
  entries: LeaderboardEntry[];
  self: LeaderboardEntry | null;
};

function formatTime(ms?: number): string | null {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return null;
  return `${(ms / 1000).toFixed(1)}s`;
}

export class LeaderboardScene extends Phaser.Scene {
  private statusText!: Phaser.GameObjects.Text;
  private lines: Phaser.GameObjects.Text[] = [];
  private meLine!: Phaser.GameObjects.Text;

  constructor() {
    super("LeaderboardScene");
  }

  create(): void {
    const { width, height } = this.scale;

    // Kill any old text objects if the scene is being reused
    this.lines.forEach((line) => line.destroy());
    this.lines = [];

    this.cameras.main.setBackgroundColor("#000000");

    this.add
      .text(width / 2, height * 0.12, "Leaderboard", {
        fontFamily: "monospace",
        fontSize: "36px",
        color: "#ffffff",
      })
      .setOrigin(0.5);

    this.statusText = this.add
      .text(width / 2, height * 0.22, "Loading scores...", {
        fontFamily: "monospace",
        fontSize: "16px",
        color: "#aaaaaa",
      })
      .setOrigin(0.5);

    const top = height * 0.3;
    const lineSpacing = 22;

    for (let i = 0; i < 10; i++) {
      const line = this.add
        .text(width / 2, top + i * lineSpacing, "", {
          fontFamily: "monospace",
          fontSize: "16px",
          color: "#ffffff",
        })
        .setOrigin(0.5, 0);
      this.lines.push(line);
    }

    this.meLine = this.add
      .text(width / 2, top + 10 * lineSpacing + 10, "", {
        fontFamily: "monospace",
        fontSize: "16px",
        color: "#ffff66",
      })
      .setOrigin(0.5, 0);

    // Menu button
    const menuWidth = 140;
    const menuHeight = 40;
    const menuBg = this.add
      .rectangle(width / 2, height * 0.9, menuWidth, menuHeight, 0xffcc33)
      .setOrigin(0.5);
    menuBg.setStrokeStyle(2, 0x000000);
    menuBg.setInteractive({ useHandCursor: true });

    const menuLabel = this.add.text(0, 0, "Menu", {
      fontFamily: "monospace",
      fontSize: "20px",
      color: "#000000",
    });

    menuLabel.setPosition(
      menuBg.x - menuLabel.width / 2,
      menuBg.y - menuLabel.height / 2
    );

    menuBg.on("pointerup", () => {
      this.scene.start("MenuScene");
    });

    this.loadLeaderboard();
  }

  private async loadLeaderboard(): Promise<void> {
    try {
      const res = await fetch("/api/leaderboard", { method: "GET" });
      if (!res.ok) {
        console.error("Leaderboard HTTP error", res.status, res.statusText);
        this.statusText.setText("Failed to load leaderboard.");
        return;
      }

      const data = (await res.json()) as LeaderboardResponse;
      const entries = data.entries ?? [];

      if (entries.length === 0) {
        this.statusText.setText("No scores yet. Play a game!");
      } else {
        this.statusText.setText("");
      }

      // Render top list exactly as provided
      for (let i = 0; i < this.lines.length; i++) {
        const lineObj = this.lines[i];
        const entry = entries[i];
        if (entry) {
          const timeStr = formatTime(entry.timeMs);
          const details = timeStr
            ? `${entry.score} (${timeStr})`
            : `${entry.score}`;

          lineObj.setText(
            `${entry.rank.toString().padStart(2, " ")}. ${
              entry.userName ?? "Player"
            } — ${details}`
          );
        } else {
          lineObj.setText("");
        }
      }

      // Compute "You" rank from the list when possible
      if (data.self) {
        const self = data.self;

        let derivedRank: number | null = null;

        const exact = entries.find(
          (e) =>
            (e.userName ?? "") === (self.userName ?? "") &&
            e.score === self.score
        );
        if (exact) {
          derivedRank = exact.rank;
        } else {
          const byName = entries.find(
            (e) => (e.userName ?? "") === (self.userName ?? "")
          );
          if (byName) {
            derivedRank = byName.rank;
          } else {
            const byScore = entries.find((e) => e.score === self.score);
            if (byScore) derivedRank = byScore.rank;
          }
        }

        const rankToShow = derivedRank ?? self.rank;
        const timeStr = formatTime(self.timeMs);
        const details = timeStr
          ? `${self.score} (${timeStr})`
          : `${self.score}`;

        this.meLine.setText(`You: #${rankToShow} — ${details}`);
      } else {
        this.meLine.setText("");
      }
    } catch (err) {
      console.error("Failed to load leaderboard", err);
      this.statusText.setText("Failed to load leaderboard.");
    }
  }
}
